import { UserRoundPen } from "lucide-react";
import { useState } from "react";

import { Button } from "@/components/ui/button";
import {
  Dialog,
  DialogContent,
  DialogFooter,
  DialogHeader,
  DialogPanel,
  DialogTitle,
} from "@/components/ui/dialog";
import { Tooltip, TooltipContent, TooltipTrigger } from "@/components/ui/tooltip";
import { AdminCombobox } from "@/features/search/components/admin-combobox";

interface TaskReassignButtonProps {
  task: { title: string; assignedAdminId: string };
  onReassign: (assignedAdminId: string) => void;
}

/** Ghost icon action: pick another admin for an open task — title and due date stay as they are. */
export function TaskReassignButton({ task, onReassign }: TaskReassignButtonProps) {
  const [open, setOpen] = useState(false);
  const [adminId, setAdminId] = useState<string | null>(null);

  function openDialog() {
    setAdminId(task.assignedAdminId);
    setOpen(true);
  }

  function confirm() {
    if (!adminId) {
      return;
    }
    setOpen(false);
    onReassign(adminId);
  }

  return (
    <>
      <Tooltip>
        <TooltipTrigger
          render={
            <Button
              variant="ghost"
              size="icon-sm"
              className="text-gray-500 hover:text-blue-600"
              aria-label="Réassigner la tâche"
              onClick={(event) => {
                event.stopPropagation();
                openDialog();
              }}
            />
          }
        >
          <UserRoundPen />
        </TooltipTrigger>
        <TooltipContent>Réassigner</TooltipContent>
      </Tooltip>

      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent onClick={(event) => event.stopPropagation()}>
          <DialogHeader>
            <DialogTitle>Réassigner la tâche</DialogTitle>
          </DialogHeader>
          <DialogPanel className="space-y-2 text-left">
            <p className="text-sm text-gray-600">{task.title}</p>
            <AdminCombobox value={adminId} onChange={setAdminId} label="Nouvel assigné" />
          </DialogPanel>
          <DialogFooter>
            <Button variant="outline" onClick={() => setOpen(false)}>
              Annuler
            </Button>
            <Button disabled={!adminId || adminId === task.assignedAdminId} onClick={confirm}>
              Réassigner
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </>
  );
}
